import React, { useState, useEffect } from 'react';
import { getSymbols, getExpiryDates, getLeadingStocksCatalogue, getKiteLoginUrl } from '../services/apiService';
import StockPicker from '../components/StockPicker';
import '../styles/LoginPage.css';
import logo from '../resources/BSG_Traders.png';

const LEAD_STOCK_INDICES = ['NIFTY', 'BANKNIFTY', 'SENSEX', 'BANKEX'];

// Fallback: default implemented stocks per index — mirrors WeightedStockService.implemented=true.
// Used when the catalogue API is unreachable (backend down, quick-start before connection).
const DEFAULT_LEAD_STOCKS = {
  NIFTY:     ['HDFCBANK', 'RELIANCE', 'ICICIBANK'],
  BANKNIFTY: ['HDFCBANK', 'ICICIBANK'],
  SENSEX:    ['HDFCBANK', 'RELIANCE', 'ICICIBANK'],
  BANKEX:    ['HDFCBANK', 'ICICIBANK'],
};

const QUICK_START_SYMBOLS = ['NIFTY', 'BANKNIFTY'];

const LoginPage = ({ onLogin, user, onLogout }) => {
  const [indices, setIndices] = useState([]);
  const [stocks, setStocks] = useState([]);
  // Derive allowed modes from user access privilege
  const access = user?.access || 'BOTH'; // ADMIN gets BOTH
  const canIndex = access === 'INDEX' || access === 'BOTH' || user?.role === 'ADMIN';
  const canStock = access === 'STOCK' || access === 'BOTH' || user?.role === 'ADMIN';
  const defaultMode = canIndex ? 'Index' : 'Equity';

  const [optionMode, setOptionMode] = useState(defaultMode);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [expiryDates, setExpiryDates] = useState([]);
  const [expiryDate, setExpiryDate] = useState('');
  const [strikePrice, setStrikePrice] = useState('');
  const [support, setSupport] = useState('');
  const [resistance, setResistance] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(5000); // stored in ms — dashboard refresh only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Lead stocks picker
  const [stockCatalogue, setStockCatalogue] = useState([]);
  const [selectedLeadStocks, setSelectedLeadStocks] = useState([]);

  const fetchSymbols = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSymbols();

      // Temporarily replace NIFTY 50 with NIFTY until backend is updated
      const processedIndices = (data.indices || []).map(index => ({
        ...index,
        symbol: index.symbol === 'NIFTY 50' ? 'NIFTY' : index.symbol
      }));

      setIndices(processedIndices);
      setStocks(data.stocks || []);
      if (processedIndices && processedIndices.length > 0) {
        setSelectedSymbol(processedIndices[0].symbol);
      }
    } catch (err) {
      setError('Failed to load symbols. Make sure the backend is running, then retry.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSymbols();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch expiry dates when symbol changes
  useEffect(() => {
    const fetchExpiryDates = async () => {
      if (selectedSymbol) {
        console.log('🔄 Starting to fetch expiry dates for:', selectedSymbol, 'type:', optionMode);
        try {
          console.log('📡 Calling API: getExpiryDates(', selectedSymbol, ',', optionMode, ')');
          const dates = await getExpiryDates(selectedSymbol, optionMode);
          console.log('✅ API Response received:', dates);
          console.log('📊 Dates array length:', dates ? dates.length : 'null/undefined');

          if (dates && dates.length > 0) {
            console.log('🎯 Setting expiry dates:', dates);
            setExpiryDates(dates);
            setExpiryDate(dates[0]); // Select first expiry date by default
            console.log('✅ Selected first date:', dates[0]);
          } else {
            console.log('⚠️ API returned empty array, using hardcoded dates');
            // Use hardcoded expiry dates for testing
            const hardcodedDates = [
              '27-Mar-2026',
              '03-Apr-2026',
              '10-Apr-2026',
              '17-Apr-2026'
            ];
            console.log('🔄 Using hardcoded expiry dates:', hardcodedDates);
            setExpiryDates(hardcodedDates);
            setExpiryDate(hardcodedDates[0]);
          }
        } catch (err) {
          console.error('❌ Failed to fetch expiry dates:', err);
          console.error('❌ Error details:', err.message);
          // Use hardcoded dates on error for testing
          const hardcodedDates = [
            '27-Mar-2026',
            '03-Apr-2026',
            '10-Apr-2026',
            '17-Apr-2026'
          ];
          console.log('🔄 Using hardcoded expiry dates due to error:', hardcodedDates);
          setExpiryDates(hardcodedDates);
          setExpiryDate(hardcodedDates[0]);
        }
      } else {
        console.log('ℹ️ No symbol selected, clearing expiry dates');
        setExpiryDates([]);
        setExpiryDate('');
      }
    };

    // Fetch expiry dates from API
    fetchExpiryDates();
  }, [selectedSymbol, optionMode]);

  // Fetch lead-stock catalogue when a supported index is selected
  useEffect(() => {
    if (optionMode !== 'Index' || !selectedSymbol || !LEAD_STOCK_INDICES.includes(selectedSymbol)) {
      setStockCatalogue([]);
      setSelectedLeadStocks([]);
      return;
    }
    getLeadingStocksCatalogue(selectedSymbol).then(data => {
      setStockCatalogue(data || []);
      const fromCatalogue = (data || []).filter(s => s.implemented).map(s => s.symbol);
      const preSelected = fromCatalogue.length > 0
        ? fromCatalogue
        : (DEFAULT_LEAD_STOCKS[selectedSymbol] || []);
      setSelectedLeadStocks(preSelected);
    });
  }, [selectedSymbol, optionMode]);

  // One-click start: auto-detect nearest expiry, use ATM + OI walls for strike/S/R
  const [quickLoading, setQuickLoading] = useState(null); // symbol being quick-started
  const handleQuickStart = async (symbol) => {
    setQuickLoading(symbol);
    setError(null);
    try {
      const dates = await getExpiryDates(symbol, 'Index');
      const expiry = dates && dates.length > 0 ? dates[0] : null;
      if (!expiry) {
        setError('Could not fetch expiry dates — check backend connection.');
        return;
      }
      // Pre-select implemented stocks; fall back to hardcoded defaults if catalogue unavailable
      const qs_catalogue = await getLeadingStocksCatalogue(symbol).catch(() => []);
      const fromCatalogue = qs_catalogue.filter(s => s.implemented).map(s => s.symbol);
      const qs_preSelected = fromCatalogue.length > 0
        ? fromCatalogue
        : (DEFAULT_LEAD_STOCKS[symbol] || []);
      onLogin({
        optionMode: 'Index',
        symbol,
        expiryDate: expiry,
        strikePrice: 0,     // AnalyzerPage auto-updates to live ATM on first poll
        support: 0,         // AnalyzerPage auto-updates from put-wall OI
        resistance: 0,      // AnalyzerPage auto-updates from call-wall OI
        refreshInterval: 5000,
        autoMode: true,
        leadStocks: qs_preSelected,
      });
    } catch (err) {
      setError('Quick start failed — check backend connection.');
    } finally {
      setQuickLoading(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedSymbol || !expiryDate || !strikePrice || !support || !resistance) {
      setError('Please fill all fields');
      return;
    }

    const config = {
      optionMode,
      symbol: selectedSymbol,
      expiryDate,
      strikePrice: parseFloat(strikePrice),
      support: parseFloat(support),
      resistance: parseFloat(resistance),
      refreshInterval: parseInt(refreshInterval),
      leadStocks: selectedLeadStocks,
    };

    onLogin(config);
  };

  const symbols = optionMode === 'Index' ? indices : stocks;

  if (loading) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="brand-header">
            <img src={logo} alt="BSG Traders" className="brand-logo" />
            <h1>BSG Traders</h1>
          </div>
          <p>Loading symbols...</p>
        </div>
      </div>
    );
  }

  if (error && indices.length === 0) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="brand-header">
            <img src={logo} alt="BSG Traders" className="brand-logo" />
            <h1>BSG Traders</h1>
          </div>
          <div className="error-message">{error}</div>
          <button className="submit-button" onClick={fetchSymbols} style={{ marginTop: '16px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        {/* ── User bar ──────────────────────────────────────────────── */}
        {user && (
          <div className="login-user-bar">
            <span className="login-user-info">
              Signed in as <strong>{user.username}</strong>
              <span className={`login-role-badge login-role-${user.role?.toLowerCase()}`}>
                {user.role}
              </span>
            </span>
            <button className="login-signout-btn" onClick={onLogout}>Sign out</button>
          </div>
        )}

        <h1>BSG Traders</h1>
        <p className="version">v1.0</p>

        {/* ── Quick Start ─────────────────────────────────────────────── */}
        {canIndex && <div className="quick-start-section">
          <div className="quick-start-label">Quick Start</div>
          <div className="quick-start-buttons">
            {QUICK_START_SYMBOLS.map(sym => (
              <button
                key={sym}
                type="button"
                className="quick-start-btn"
                onClick={() => handleQuickStart(sym)}
                disabled={quickLoading != null}
              >
                {quickLoading === sym ? 'Starting…' : `▶ ${sym}`}
              </button>
            ))}
          </div>
          <div className="quick-start-note">
            Auto-detects current expiry · ATM strike · OI-based S/R levels
          </div>
        </div>}
        {canIndex && <div className="quick-start-divider"><span>or configure manually</span></div>}

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Mode:</label>
            <select
              value={optionMode}
              onChange={(e) => {
                setOptionMode(e.target.value);
                setSelectedSymbol('');
                setExpiryDates([]);
                setExpiryDate('');
              }}
              className="form-input"
            >
              {canIndex && <option value="Index">Index</option>}
              {canStock && <option value="Equity">Stock</option>}
            </select>
          </div>

          <div className="form-group">
            <label>{optionMode}:</label>
            <select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="form-input"
              required
            >
              <option value="">Select {optionMode}</option>
              {symbols.map((symbol) => (
                <option key={symbol.symbol} value={symbol.symbol}>
                  {symbol.symbol}{symbol.exchange === 'BSE' ? ' (BSE)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* ── Lead Stocks picker — only for NIFTY / BANKNIFTY / SENSEX ── */}
          {optionMode === 'Index' && stockCatalogue.length > 0 && (
            <div className="form-group">
              <label className="lead-stocks-label">
                Lead Stocks to Monitor
                <span className="lead-stocks-sublabel"> — sorted by index weight</span>
              </label>
              <StockPicker
                catalogue={stockCatalogue}
                selected={selectedLeadStocks}
                onChange={setSelectedLeadStocks}
              />
              <div className="lead-stocks-note">
                ● pre-selected = already wired into analysis engine
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Expiry Date:</label>
            <select
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="form-input"
              required
              disabled={!selectedSymbol}
            >
              <option value="">
                {selectedSymbol 
                  ? (expiryDates.length === 0 ? 'Loading expiry dates...' : 'Select expiry date') 
                  : 'Select a symbol first'}
              </option>
              {expiryDates.map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Strike Price:</label>
            <input
              type="number"
              value={strikePrice}
              onChange={(e) => setStrikePrice(e.target.value)}
              className="form-input"
              placeholder="Enter strike price"
              step="1"
              required
            />
          </div>

          <div className="form-group">
            <label>Support:</label>
            <input
              type="number"
              value={support}
              onChange={(e) => setSupport(e.target.value)}
              className="form-input"
              placeholder="Enter support"
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label>Resistance:</label>
            <input
              type="number"
              value={resistance}
              onChange={(e) => setResistance(e.target.value)}
              className="form-input"
              placeholder="Enter resistance"
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label>Refresh Interval:</label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(e.target.value)}
              className="form-input"
            >
              <optgroup label="Seconds">
                <option value="5000">5 seconds</option>
                <option value="10000">10 seconds</option>
                <option value="15000">15 seconds</option>
                <option value="20000">20 seconds</option>
                <option value="25000">25 seconds</option>
                <option value="30000">30 seconds</option>
                <option value="35000">35 seconds</option>
                <option value="40000">40 seconds</option>
                <option value="45000">45 seconds</option>
                <option value="50000">50 seconds</option>
                <option value="55000">55 seconds</option>
              </optgroup>
              <optgroup label="Minutes">
                <option value="60000">1 minute</option>
                <option value="120000">2 minutes</option>
                <option value="180000">3 minutes</option>
                <option value="300000">5 minutes</option>
                <option value="600000">10 minutes</option>
                <option value="900000">15 minutes</option>
              </optgroup>
            </select>
          </div>

          <button type="submit" className="submit-button">
            Start Analysis
          </button>
        </form>

        {/* ── Kite Connect — admin only ─────────────────────────── */}
        {user?.role === 'ADMIN' && (<>
          <div className="kite-divider"><span>or trade via Zerodha</span></div>
          <button
            type="button"
            className="kite-connect-btn"
            onClick={async () => {
              try {
                const url = await getKiteLoginUrl(user.username);
                window.location.href = url;
              } catch {
                alert('Could not reach backend to get Kite login URL.');
              }
            }}
          >
            <span className="kite-z-badge">Z</span>
            Connect Zerodha Kite
          </button>
        </>)}
      </div>
    </div>
  );
};

export default LoginPage;
